import axios from 'axios'
import { toast } from 'vue3-toastify'
import { getAccessToken, loginWithRedirect } from './auth'

// Get API URL from runtime config or environment variable
const getApiUrl = () => {
  if (typeof window !== 'undefined' && window.APP_CONFIG?.VITE_API_URL) {
    return window.APP_CONFIG.VITE_API_URL
  }
  return import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
}

// Create axios instance
const api = axios.create({
  baseURL: getApiUrl(),
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Create axios instance with longer timeout for operations that may take longer (e.g., Mailgun API calls)
const apiLongTimeout = axios.create({
  baseURL: getApiUrl(),
  timeout: 60000, // 1 minute
  headers: {
    'Content-Type': 'application/json'
  }
})

// Apply same interceptors to long timeout instance
apiLongTimeout.interceptors.request.use(
  async (config) => {
    // Ensure headers object exists
    if (!config.headers) {
      config.headers = {}
    }
    
    // Add partner ID from localStorage to all requests
    const selectedPartnerId = localStorage.getItem('selectedPartnerId')
    if (selectedPartnerId) {
      config.headers['X-Partner-ID'] = selectedPartnerId
    }
    
    // If headers already contain Authorization, don't override
    if (!config.headers.Authorization && !config.headers.authorization) {
      try {
        const token = await getAccessToken()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
          console.log('Authorization header added to request:', config.url)
        } else {
          console.error('No token available for request:', config.url)
          return Promise.reject(new Error('No authentication token available'))
        }
      } catch (error) {
        console.error('Could not get auth token for request:', config.url, error)
        
        // If it's a redirect error (missing refresh token), don't reject the request
        // The redirect will happen and the user will be able to retry after login
        if (error.message?.includes('Redirecting to login')) {
          // Return a special error that won't trigger the response interceptor's error handling
          return Promise.reject(error)
        }
        
        // Reject the request if we can't get a token
        return Promise.reject(new Error(`Failed to get authentication token: ${error.message}`))
      }
    } else {
      console.log('Authorization header already present for request:', config.url)
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

apiLongTimeout.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    const message = error.response?.data?.error || error.message || 'An error occurred'
    
    // Show error toast
    toast.error(message)
    
    // Handle specific error cases
    if (error.response?.status === 401) {
      // Unauthorized - redirect to login
      loginWithRedirect()
    }
    
    return Promise.reject(error)
  }
)

// Request interceptor to add auth token and partner ID
api.interceptors.request.use(
  async (config) => {
    // Ensure headers object exists
    if (!config.headers) {
      config.headers = {}
    }
    
    // Add partner ID from localStorage to all requests
    const selectedPartnerId = localStorage.getItem('selectedPartnerId')
    if (selectedPartnerId) {
      config.headers['X-Partner-ID'] = selectedPartnerId
    }
    
    // If headers already contain Authorization, don't override
    if (!config.headers.Authorization && !config.headers.authorization) {
      try {
        const token = await getAccessToken()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
          console.log('Authorization header added to request:', config.url)
        } else {
          console.error('No token available for request:', config.url)
          return Promise.reject(new Error('No authentication token available'))
        }
      } catch (error) {
        console.error('Could not get auth token for request:', config.url, error)
        
        // If it's a redirect error (missing refresh token), don't reject the request
        // The redirect will happen and the user will be able to retry after login
        if (error.message?.includes('Redirecting to login')) {
          // Return a special error that won't trigger the response interceptor's error handling
          return Promise.reject(error)
        }
        
        // Reject the request if we can't get a token
        // This will prevent unauthorized requests from being sent
        return Promise.reject(new Error(`Failed to get authentication token: ${error.message}`))
      }
    } else {
      console.log('Authorization header already present for request:', config.url)
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    const message = error.response?.data?.error || error.message || 'An error occurred'
    
    // Show error toast
    toast.error(message)
    
    // Handle specific error cases
    if (error.response?.status === 401) {
      // Unauthorized - redirect to login
      loginWithRedirect()
    }
    
    return Promise.reject(error)
  }
)

// API methods
export const campaignsApi = {
  getAll: () => api.get('/campaigns'),
  getById: (id) => api.get(`/campaigns/${id}`),
  create: (data) => api.post('/campaigns', data),
  update: (id, data) => api.put(`/campaigns/${id}`, data),
  delete: (id) => api.delete(`/campaigns/${id}`),
  pause: (id) => api.post(`/campaigns/${id}/pause`),
  resume: (id) => api.post(`/campaigns/${id}/resume`),
  refreshStats: (id) => apiLongTimeout.post(`/campaigns/${id}/refresh-stats`),
  getSends: (id) => api.get(`/campaigns/${id}/sends`)
}

export const listsApi = {
  getAll: () => api.get('/lists'),
  getById: (id) => api.get(`/lists/${id}`),
  create: (data) => api.post('/lists', data),
  update: (id, data) => api.put(`/lists/${id}`, data),
  delete: (id) => api.delete(`/lists/${id}`),
  addSubscriber: (listId, subscriberData) => api.post(`/lists/${listId}/subscribers`, subscriberData),
  addSubscribersBulk: (listId, subscribers) => api.post(`/lists/${listId}/subscribers/bulk`, { subscribers }),
  removeSubscriber: (listId, subscriberId) => api.delete(`/lists/${listId}/subscribers/${subscriberId}`),
  // Legacy method - kept for backward compatibility but should use addSubscribersBulk instead
  addSubscribers: async (listId, emails) => {
    const subscribers = emails.map(email => ({ email }))
    return api.post(`/lists/${listId}/subscribers/bulk`, { subscribers })
  }
}

export const domainsApi = {
  getAll: () => api.get('/domains'),
  getById: (id) => api.get(`/domains/${id}`),
  create: (data) => api.post('/domains', data),
  update: (id, data) => api.put(`/domains/${id}`, data),
  delete: (id) => api.delete(`/domains/${id}`),
  sync: () => api.post('/domains/sync'),
  refreshScore: (id) => api.post(`/domains/${id}/refresh-score`),
  getTemplates: (domainId) => api.get(`/domains/${domainId}/templates`),
  syncTemplates: (domainId) => api.post(`/domains/${domainId}/templates/sync`),
  updateTracking: (id, data) => api.put(`/domains/${id}/tracking`, data)
}

export const clubsApi = {
  getAll: () => api.get('/clubs'),
  getById: (id) => api.get(`/clubs/${id}`),
  create: (data) => api.post('/clubs', data),
  update: (id, data) => api.put(`/clubs/${id}`, data),
  delete: (id) => api.delete(`/clubs/${id}`)
}

export const settingsApi = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data)
}

export const mailgunApi = {
  getTemplates: () => api.get('/mailgun/templates'),
  getTemplate: (name) => api.get(`/mailgun/templates/${name}`),
  createTemplate: (data) => api.post('/mailgun/templates', data),
  updateTemplate: (name, data) => api.put(`/mailgun/templates/${name}`),
  deleteTemplate: (name) => api.delete(`/mailgun/templates/${name}`)
}

export const partnersApi = {
  getMe: () => api.get('/partners/me'),
  getAccessible: () => api.get('/partners/accessible'),
  getById: (id) => api.get(`/partners/${id}`),
  create: (data) => api.post('/partners', data),
  update: (id, data) => api.put(`/partners/${id}`, data)
}

export default api 